import { memo, useContext } from 'react';
import { NodeToolbar, useReactFlow } from '@xyflow/react';
import { NODES_TYPES, CLEAR_SELECTED_NODE_EVENT } from '../../types';
import {
  useDeleteOneActionTargetMutation,
  useDeleteOneEventActionMutation,
  useDeleteOneTimelineEventMutation,
  useDeleteOneEventConditionMutation,
} from '@src/components/generated/graphql';
import { DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { ThemeContext } from '../../../../../../pages/_app';
import styles from '../../timeline-node-manager.module.css';

type CustomNodeProps = {
  id: string;
  data: any;
  type: NODES_TYPES;
  label: string;
  subtitle?: string;
  children: React.ReactNode;
  containerClassname?: string;
  selected?: boolean;
};

const getTooltipText = (type: NODES_TYPES) => {
  switch (type) {
    case NODES_TYPES.CONDITION:
      return 'When should this event trigger?';
    case NODES_TYPES.TARGET:
      return 'What objects should be affected?';
    default:
      return '';
  }
};

const CustomNode = ({
  id,
  data,
  type,
  label,
  subtitle,
  children,
  containerClassname,
  selected = false,
}: CustomNodeProps) => {
  const { setNodes, setEdges } = useReactFlow();
  const { isDark } = useContext(ThemeContext);
  const [deleteEventNode] = useDeleteOneTimelineEventMutation();
  const [deleteActionNode] = useDeleteOneEventActionMutation();
  const [deleteTargetNode] = useDeleteOneActionTargetMutation();
  const [deleteConditionNode] = useDeleteOneEventConditionMutation();

  const handleDelete = async () => {
    //TODO: Determine before that this only local and do not delete it
    try {
      switch (type) {
        case NODES_TYPES.QUEST:
        case NODES_TYPES.EVENT:
          await setNodes((nds) =>
            nds.map((n) =>
              n?.data?.eventId === id
                ? {
                    ...n,
                    data: {
                      ...n.data,
                      isOnComplete: undefined,
                      onCompleteConditionId: undefined,
                      eventId: undefined,
                    },
                  }
                : n
            )
          );
          await deleteEventNode({ variables: { where: { id } } });
          break;
        case NODES_TYPES.ACTION:
          await deleteActionNode({ variables: { where: { id } } });
          break;
        case NODES_TYPES.CONDITION:
        case NODES_TYPES.DESTROYABLE:
        case NODES_TYPES.COLLECTIBLE:
          await setNodes((nds) =>
            nds.map((n) =>
              n?.data?.onCompleteConditionId === id
                ? {
                    ...n,
                    data: {
                      ...n.data,
                      isOnComplete: undefined,
                      onCompleteConditionId: undefined,
                    },
                  }
                : n
            )
          );
          await deleteConditionNode({ variables: { where: { id } } });
          break;
        case NODES_TYPES.TARGET:
          await deleteTargetNode({ variables: { where: { id } } });
          break;
        case NODES_TYPES.EVENT_LINK:
          await deleteConditionNode({ variables: { where: { id } } });
          break;
        default:
          break;
      }
    } catch (err) {
      console.warn(
        'Missing node to delete, seems to be created locally only: ',
        err
      );
    }

    setNodes((nds) => nds.filter((node) => node.id !== id));
    setEdges((eds) =>
      eds.filter((edge) => edge.source !== id && edge.target !== id)
    );
    window.dispatchEvent(new CustomEvent(CLEAR_SELECTED_NODE_EVENT));
  };

  const tooltipText = getTooltipText(type);

  return (
    <div
      className={
        containerClassname
          ? `${styles.nodeEvent} ${containerClassname} ${
              selected ? styles.selected : ''
            } ${isDark ? 'dark' : ''}`
          : `${styles.nodeEvent} ${selected ? styles.selected : ''} ${
              isDark ? 'dark' : ''
            }`
      }
      style={{
        backgroundColor: isDark ? '#1E1F1F' : '#FFF',
        borderColor: isDark ? '#2E2F2F' : '#a3a3a3',
      }}
    >
      <NodeToolbar
        isVisible={data.toolbarVisible}
        position={data.toolbarPosition}
      >
        <button
          onClick={handleDelete}
          style={{ color: isDark ? '#fff' : '#000' }}
        >
          <DeleteOutlined />
        </button>
      </NodeToolbar>
      <div>
        <h1
          className={styles.nodeLabel}
          style={{ color: isDark ? '#fff' : '#000' }}
        >
          {label}
          {tooltipText && (
            <Tooltip title={tooltipText}>
              <QuestionCircleOutlined
                style={{ marginLeft: '8px', color: isDark ? '#666' : '#999' }}
              />
            </Tooltip>
          )}
        </h1>
        {subtitle && (
          <p
            className={styles.nodeSubtitle}
            style={{ color: isDark ? '#999' : '#666' }}
          >
            {subtitle}
          </p>
        )}
        <hr
          className={styles.nodeLabelHr}
          style={{ borderColor: isDark ? '#333' : '#e0e0e0' }}
        />
      </div>
      {children}
    </div>
  );
};

export default memo(CustomNode);
